/**
 * Single, consistent tooltip for all copy actions.
 * Desktop: shows "Copy" / "Copy ID" / "Copy link" on hover and "Copied!" after copy.
 * Mobile: only shows "Copied!" after tap (no hover, so no "Copy" label).
 */

import { ReactElement, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";

const COPIED_LABEL = "Copied!";

export interface CopyFeedbackTooltipProps {
  /** Label when not copied (e.g. "Copy", "Copy ID", "Copy link"); not shown on mobile. */
  copyLabel: string;
  /** Unique key for this copy button; when lastCopiedKey matches, show "Copied!". */
  feedbackKey: string;
  /** Key set by parent after a copy (e.g. from showCopiedFeedback); null when none. */
  lastCopiedKey: string | null;
  /** When true, only show tooltip for "Copied!" after tap; never show "Copy" / "Copy ID". */
  isMobile?: boolean;
  /** Single element (e.g. IconButton or Button) that can hold a ref. */
  children: ReactElement;
}

export default function CopyFeedbackTooltip({
  copyLabel,
  feedbackKey,
  lastCopiedKey,
  isMobile = false,
  children,
}: CopyFeedbackTooltipProps) {
  const showCopied = lastCopiedKey === feedbackKey;
  const [title, setTitle] = useState(showCopied ? COPIED_LABEL : copyLabel);

  useEffect(() => {
    if (showCopied) setTitle(COPIED_LABEL);
  }, [showCopied]);

  if (isMobile && !showCopied) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      title={title}
      open={showCopied}
      placement="top"
      onClose={() => setTitle(copyLabel)}
      disableHoverListener={showCopied}
      disableFocusListener={showCopied}
      slotProps={{
        tooltip: {
          sx: {
            fontSize: "0.8125rem",
            lineHeight: 1.4,
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
}
